import React from "react";

/**
 * Terms & Conditions — Vybly Dynamics Inc.
 * Tracks the comprehensive TermsFeed structure you supplied, while adding:
 * - 18+ eligibility; wellness/no-professional-advice; AI outputs may be inaccurate.
 * - Public Hive‑Mind vs private reflections licenses.
 * - Flexible billing (one‑off charges, credits, memberships, bundles, subscriptions), pricing changes, taxes, refunds.
 * - Delaware law; AAA consumer arbitration; 30‑day email opt‑out; class/rep waiver; small-claims carve‑out.
 */
export default function TermsOfService() {
  return (
    <main className="legal-page terms" style={{ maxWidth: 960, margin: "40px auto", lineHeight: 1.6 }}>
      <h1>Terms and Conditions</h1>
      <p><strong>Last updated:</strong> September 10, 2025</p>
      <p>Please read these terms and conditions carefully before using our Service.</p>

      <h2>Interpretation and Definitions</h2>
      <h3>Interpretation</h3>
      <p>
        Words with the initial letter capitalized have meanings defined below. The following definitions shall have the same meaning regardless of whether they
        appear in singular or in plural.
      </p>

      <h3>Definitions</h3>
      <ul>
        <li><p><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where “control” means ownership of
          50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</p></li>
        <li><p><strong>Account</strong> means a unique account created for you to access our Service or parts of our Service.</p></li>
        <li><p><strong>Country</strong> refers to California, United States.</p></li>
        <li><p><strong>Company</strong> (referred to as “the Company”, “we”, “us” or “our”) refers to Vybly Dynamics Inc., a Delaware corporation operating in California, USA.</p></li>
        <li><p><strong>Content</strong> refers to content such as text, images, or other information that can be posted, uploaded, linked to or otherwise made available by you.</p></li>
        <li><p><strong>Device</strong> means any device that can access the Service such as a computer, phone or tablet.</p></li>
        <li><p><strong>Feedback</strong> means feedback, innovations or suggestions sent by you about the Service.</p></li>
        <li><p><strong>Free Trial</strong> refers to a limited period that may be free or discounted (e.g., trial credit) when joining or purchasing a plan.</p></li>
        <li><p><strong>Goods</strong> refer to items or digital goods offered for sale on the Service.</p></li>
        <li><p><strong>Orders</strong> mean a request by you to purchase Goods or Services from us.</p></li>
        <li><p><strong>Promotions</strong> refer to contests, sweepstakes or other promotions offered through the Service.</p></li>
        <li><p><strong>Service</strong> refers to our website and services, including wellness prompts, journaling, brainstorming, Frameworks sessions, and Hive‑Mind.</p></li>
        <li><p><strong>Subscriptions</strong> refer to services or access offered on a recurring basis; <strong>Memberships</strong> include bundled features or benefits; <strong>Credits</strong> refer to timed or unit‑based access (e.g., one‑hour sessions); we may also offer <strong>one‑off charges</strong> and <strong>packages</strong>.</p></li>
        <li><p><strong>Third‑party Social Media Service</strong> means any third‑party services or content integrated with the Service.</p></li>
        <li><p><strong>Website</strong> refers to Vybly, accessible from <a href="https://www.vybly.net/" target="_blank" rel="noopener noreferrer">https://www.vybly.net/</a>.</p></li>
        <li><p><strong>You</strong> means the individual using the Service, or the company/other legal entity on whose behalf it is used.</p></li>
      </ul>

      <h2>Acknowledgment</h2>
      <p>
        These Terms govern your use of the Service and form a binding agreement between you and the Company. By accessing or using the Service you agree to be
        bound by these Terms and our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. If you do not agree, do not use the Service.
      </p>

      <h2>Eligibility (18+)</h2>
      <p>The Service is intended for individuals <strong>18 years of age or older</strong>. By using the Service, you represent and warrant that you are 18+.</p>

      <h2>Wellness &amp; No Professional Advice</h2>
      <p>
        Vybly provides wellness prompts, journaling, and brainstorming tools. <strong>We are not a health care provider, therapist, crisis service, legal
        advisor, financial advisor, or other professional advisor.</strong> Information and outputs (including AI‑generated content) are for informational and
        self‑reflection purposes only and may be inaccurate or incomplete. <strong>Do not rely on the Service for medical, mental‑health, legal, financial,
        or other professional advice.</strong> If you are in crisis, call 911 (or your local emergency number) or a local crisis hotline immediately.
      </p>

      <h2>AI &amp; Model Outputs</h2>
      <p>
        Parts of the Service use artificial intelligence to generate or analyze text. AI systems can make mistakes, reflect bias, or produce unexpected results.
        You are responsible for evaluating outputs and using your own judgment.
      </p>

      <h2>User Accounts</h2>
      <p>
        You must provide accurate information and keep your credentials secure. You are responsible for activities under your account. Notify us at{" "}
        <a href="mailto:info@vybly.net">info@vybly.net</a> of unauthorized use. We may require multi‑factor authentication or take steps to protect the Service.
      </p>

      <h2>Content</h2>
      <h3>Your Right to Post Content</h3>
      <p>
        Our Service allows you to post Content. You are responsible for the Content you post, including its legality and appropriateness. By posting Content, you
        grant us the rights described below and you represent that you have all necessary rights to do so.
      </p>

      <h3>Private Content (Reflections &amp; Frameworks)</h3>
      <p>
        For private reflections and Frameworks session content, you retain ownership. You grant Vybly a <strong>non‑exclusive, worldwide, royalty‑free license</strong>
        to <strong>host, store, process, transmit, and display</strong> such content solely to operate, maintain, secure, and provide the Service to you and as
        otherwise directed by you. We do not publish this private content to other users unless you choose to share it.
      </p>

      <h3>Public Content (Hive‑Mind)</h3>
      <p>
        If you submit Content to areas intended to be public (e.g., Hive‑Mind), you grant Vybly a <strong>non‑exclusive, worldwide, royalty‑free, sublicensable and
        transferable license</strong> to <strong>host, store, reproduce, modify, publish, publicly display, publicly perform, and distribute</strong> that Content
        to operate, promote, and improve the Service and to display it to other users and the public. You also grant other users a license to access and use that
        Content as permitted by these Terms. Public posts may be indexed by search engines. Avoid posting confidential or sensitive information publicly.
      </p>

      <h3>Content Restrictions</h3>
      <ul>
        <li>Illegal or infringing content (including IP rights violations).</li>
        <li>Defamatory, harassing, hateful, deceptive, or privacy‑invasive content; impersonation.</li>
        <li>Malware or any activity that disrupts security or operation.</li>
        <li>Posting others’ personal data without authorization.</li>
      </ul>
      <p>We may remove or restrict content or accounts that violate these Terms or the law.</p>

      <h3>Content Backups</h3>
      <p>
        While we perform backups, we do not guarantee there will be no loss or corruption of data. You agree to maintain your own copies of Content independent
        of the Service.
      </p>

      <h2>Copyright Policy (DMCA)</h2>
      <p>
        We respect intellectual property rights. If you believe content infringes your copyright, email{" "}
        <a href="mailto:info@vybly.net">info@vybly.net</a> with a notice meeting 17 U.S.C. §512(c)(3). We may remove content and terminate repeat infringers.
      </p>

      <h2>Pricing; Credits; Memberships; Subscriptions; One‑Off Charges</h2>
      <p>
        We may offer multiple commercial models, including <strong>pay‑per‑use credits</strong> (e.g., timed sessions), <strong>memberships</strong>,{" "}
        <strong>bundles/packages</strong>, <strong>subscriptions</strong> (recurring), and <strong>one‑off charges</strong>. Pricing may vary by plan, promotion,
        market, or taxes. Taxes and fees may be added as required by law.
      </p>
      <ul>
        <li><strong>Billing &amp; Auto‑Renewal:</strong> If you purchase a subscription or membership, it will automatically renew at the then‑current price unless you cancel
          prior to renewal in your account settings or by contacting us. We may change prices with reasonable advance notice; if you do not agree, cancel before renewal.</li>
        <li><strong>Credits:</strong> Credits entitle you to specified usage (e.g., a one‑hour session). Credits have no cash value, are non‑transferable, and may expire per the plan details.</li>
        <li><strong>Free Trials/Promotions:</strong> We may offer promotional credits or trials. If billing information is collected for a trial, you will be charged at the end of
          the trial unless you cancel. We may modify or end promotions at any time as permitted by law.</li>
        <li><strong>Refunds:</strong> Except where required by law or as expressly stated, fees are non‑refundable. We may consider requests case‑by‑case at our discretion.</li>
        <li><strong>Chargebacks:</strong> If you dispute a charge that is valid, we may suspend or terminate access and/or recover costs where permitted.</li>
        <li><strong>Payment Methods:</strong> You authorize us and our payment processor (e.g., Stripe) to charge your selected method for purchases, renewals, taxes, and fees.</li>
      </ul>

      <h2>Orders for Goods</h2>
      <p>
        By placing an order, you represent that the information you provide is accurate and that you have the legal right to use the payment method. We reserve
        the right to refuse or cancel orders for reasons including availability, errors, or suspected fraud.
      </p>

      <h2>Availability, Errors and Inaccuracies</h2>
      <p>
        Offerings may be mispriced, described inaccurately, or unavailable, and we may experience delays in updating information. We reserve the right to correct
        errors and update information at any time without prior notice.
      </p>

      <h2>Links to Other Websites</h2>
      <p>
        The Service may contain links to third‑party sites not owned or controlled by us. We are not responsible for their content or practices. Review any
        third‑party terms and privacy policies you visit.
      </p>

      <h2>Feedback</h2>
      <p>
        You assign to Vybly all rights in Feedback you provide. If assignment is ineffective, you grant Vybly a non‑exclusive, perpetual, irrevocable, royalty‑free,
        worldwide license to use and exploit Feedback without restriction.
      </p>

      <h2>Indemnification</h2>
      <p>
        To the fullest extent permitted by law, you will indemnify and hold harmless Vybly and its officers, directors, employees, and agents from and against
        any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of or in any way connected with (a) your access to
        or use of the Service; (b) your Content; or (c) your violation of these Terms.
      </p>

      <h2>Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, VYBLY AND ITS SUPPLIERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
        PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, EVEN IF ADVISED OF THE POSSIBILITY. OUR AGGREGATE LIABILITY FOR
        ALL CLAIMS RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO VYBLY FOR THE SERVICE IN THE SIX (6) MONTHS BEFORE THE
        EVENT GIVING RISE TO LIABILITY OR (B) USD $100.
      </p>

      <h2>“AS IS” and “AS AVAILABLE” Disclaimer</h2>
      <p>
        THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” VYBLY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, NON‑INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE. WE DO NOT WARRANT THAT THE SERVICE OR AI OUTPUTS WILL
        BE ACCURATE, RELIABLE, OR ERROR‑FREE.
      </p>

      <h2>Governing Law; Venue</h2>
      <p>
        These Terms are governed by the laws of the <strong>State of Delaware</strong>, without regard to conflict‑of‑laws rules. Subject to the Arbitration clause
        below, the exclusive venue for disputes is the state and federal courts in <strong>Los Angeles County, California</strong>, and you consent to personal
        jurisdiction there.
      </p>

      <h2>Arbitration; Class Action Waiver</h2>
      <p>
        <strong>Binding Arbitration.</strong> Except for small‑claims court or claims seeking injunctive relief for unauthorized use of IP or the Service, any
        dispute arising from or relating to these Terms or the Service will be finally settled by binding arbitration administered by the American Arbitration
        Association (“AAA”) under its Consumer Arbitration Rules. The seat of arbitration is Los Angeles County, California.
      </p>
      <p>
        <strong>Class/Representative Waiver.</strong> YOU AND VYBLY AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY
        AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
      </p>
      <p>
        <strong>30‑Day Opt‑Out.</strong> You may opt out of arbitration by emailing <a href="mailto:info@vybly.net">info@vybly.net</a> with the subject
        “Arbitration Opt‑Out” within <strong>30 days</strong> of first accepting these Terms, including your name and the email associated with your account.
        Opting out does not affect prior proceedings.
      </p>

      <h2>Termination</h2>
      <p>
        We may suspend or terminate access to the Service (or any part) at any time for any reason, including violation of these Terms. Upon termination, your
        right to use the Service ceases immediately.
      </p>

      <h2>Force Majeure</h2>
      <p>
        We will not be liable for any delay or failure to perform resulting from causes beyond our reasonable control, including acts of God, labor disputes,
        internet failures, or governmental actions.
      </p>

      <h2>United States Legal Compliance</h2>
      <p>You represent and warrant that you are not located in a country subject to U.S. embargo or on any U.S. government list of prohibited parties.</p>

      <h2>Severability; Waiver; Assignment; Entire Agreement; Survival</h2>
      <p>
        If any provision is unenforceable, the rest remain in effect. Failure to enforce a provision is not a waiver. You may not assign these Terms without our
        consent; we may assign to an affiliate or in connection with a reorganization. These Terms (and any policies referenced) are the entire agreement between
        you and Vybly regarding the Service. Provisions that by their nature should survive termination will survive (e.g., content licenses for public posts,
        disclaimers, limitations of liability, arbitration, and indemnification).
      </p>

      <h2>Translation Interpretation</h2>
      <p>
        These Terms and Conditions may have been translated if we made them available to you on our Service.
        You agree that the original English text shall prevail in the case of a dispute.
      </p>

      <h2>Changes to These Terms</h2>
      <p>
        We may modify these Terms from time to time. If changes are material, we will provide reasonable prior notice. Continued use after the effective date
        constitutes acceptance. If you do not agree, stop using the Service and cancel any renewals before they take effect.
      </p>

      <h2>Contact Us</h2>
      <p>
        Email: <a href="mailto:info@vybly.net">info@vybly.net</a><br />
      </p>
    </main>
  );
}
